import React from 'react';
import { Check } from 'lucide-react';
import { TRANSACTION_PHASES, EXCEPTIONAL_STATUSES } from '../../../constants/transactionPhases';
import { TRANSACTION_STATUSES_LABELS } from '../../../constants/transactionStatuses';

export default function TransactionProgress({ status, compact = false }) {
  if (EXCEPTIONAL_STATUSES.includes(status)) {
    return (
      <div className={`rounded-lg border border-[var(--hp-danger-200)] bg-[var(--hp-danger-50)] px-4 py-3 text-[var(--hp-danger-800)] dark:border-[var(--hp-danger-800)] dark:bg-[var(--hp-danger-900)] dark:text-[var(--hp-danger-300)] ${compact ? 'text-xs py-2' : 'text-sm'}`}>
        Cette opération est en situation exceptionnelle : {TRANSACTION_STATUSES_LABELS[status] || status}.
      </div>
    );
  }

  // Find current phase index
  const currentPhaseIndex = TRANSACTION_PHASES.findIndex(p => p.statuses.includes(status));
  // If status is not found (shouldn't happen normally unless new statuses are added), default to 0
  const activeIndex = currentPhaseIndex >= 0 ? currentPhaseIndex : 0;

  return (
    <div className="w-full">
      {/* Mobile view: simple progress bar */}
      <div className="sm:hidden">
        <div className="flex items-center justify-between text-xs font-medium">
          <span className="text-primary">{TRANSACTION_PHASES[activeIndex]?.label}</span>
          <span className="text-subtle">
            Étape {activeIndex + 1} sur {TRANSACTION_PHASES.length}
          </span>
        </div>
        <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-surface-muted">
          <div
            className="h-full rounded-full bg-[var(--hp-info-500)] transition-all duration-500"
            style={{ width: `${((activeIndex + 1) / TRANSACTION_PHASES.length) * 100}%` }}
          />
        </div>
      </div>

      {/* Desktop view: Stepper */}
      <div className="hidden sm:block">
        <nav aria-label="Progress">
          <ol role="list" className="flex items-center">
            {TRANSACTION_PHASES.map((phase, stepIdx) => {
              const isActive = stepIdx === activeIndex;
              const isCompleted = stepIdx < activeIndex;
              
              const circleSize = compact ? "h-6 w-6" : "h-8 w-8";
              const checkSize = compact ? "h-3.5 w-3.5" : "h-5 w-5";
              const dotSize = compact ? "h-2 w-2" : "h-2.5 w-2.5";
              const linePadding = compact ? "pr-4 sm:pr-8" : "pr-8 sm:pr-20";

              return (
                <li key={phase.id} className={`relative ${stepIdx !== TRANSACTION_PHASES.length - 1 ? linePadding : ''}`} title={compact ? phase.label : undefined}>
                  {isCompleted ? (
                    <>
                      <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className="h-0.5 w-full bg-[var(--hp-info-500)]" />
                      </div>
                      <div className={`relative flex ${circleSize} items-center justify-center rounded-full bg-[var(--hp-info-500)] hover:bg-[var(--hp-info-600)] transition-colors cursor-help`}>
                        <Check className={`${checkSize} text-white`} aria-hidden="true" />
                      </div>
                    </>
                  ) : isActive ? (
                    <>
                      <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className="h-0.5 w-full bg-surface-muted" />
                      </div>
                      <div
                        className={`relative flex ${circleSize} items-center justify-center rounded-full border-2 border-[var(--hp-info-500)] bg-surface cursor-help`}
                        aria-current="step"
                      >
                        <span className={`${dotSize} rounded-full bg-[var(--hp-info-500)]`} aria-hidden="true" />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="absolute inset-0 flex items-center" aria-hidden="true">
                        <div className="h-0.5 w-full bg-surface-muted" />
                      </div>
                      <div className={`relative flex ${circleSize} items-center justify-center rounded-full border-2 border-surface-muted bg-surface cursor-help`}>
                        <span className={`${dotSize} rounded-full bg-transparent`} aria-hidden="true" />
                      </div>
                    </>
                  )}
                  {/* Label (only if not compact) */}
                  {!compact && (
                    <span
                      className={`absolute -bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-medium ${
                        isActive ? 'text-[var(--hp-info-600)] dark:text-[var(--hp-info-400)]' : isCompleted ? 'text-primary' : 'text-subtle'
                      }`}
                    >
                      {phase.label}
                    </span>
                  )}
                </li>
              );
            })}
          </ol>
        </nav>
      </div>
    </div>
  );
}
